import { useMemo } from 'react';
import type { IncomingMessage } from 'http';

import { graphql } from '@/graphql/__generated/gql';
import { UserFullProfileQuery } from '@/graphql/__generated/gql/graphql';
import { FullUser, ProfileType, UserProfile } from '@/graphql/__generated/resolvers-types';
import useRegion from '@/hooks/providers/useRegion';
import { USER_STATUS } from '@/utils/constants/userStatus';
import { prefetchRequest, useRequest } from '@/utils/request';
import {
    isAnonymousUser,
    isExSubscriberUser,
    isLoggedInWithStatus,
    isSubscriberUser,
} from '@/utils/user/user-predicates';

// Fallback profile used when `UserFullProfile` returns nothing (logged-out user, or the
// user-profiles remote failing). Mirrors a real anonymous profile so profile-derived consumers
// (DMA, listings, nav, search) resolve valid params instead of rejecting with BAD_USER_INPUT.
// `userRegistrationCountry` is a static `US` default that `buildFullUserProfile` overrides per
// request (see below). No `isLoggedIn`: page-view tracking treats this as an unresolved placeholder
// by that field's absence (see isUserDataReadyForPageView). Top-level packageCode/userState/
// billingVendor are kept for existing direct readers (e.g. hubs childCategoryUtils).
const ANON_PROFILE = {
    packageCode: 'NEW_FREE_PACKAGE',
    userState: USER_STATUS.ANONYMOUS,
    billingVendor: '',
    userStatus: { description: USER_STATUS.ANONYMOUS },
    userRegistrationCountry: 'US',
    activeProfile: { profileType: ProfileType.Adult } as UserProfile,
    cbsPackageInfo: [{ packageCode: 'NEW_FREE_PACKAGE', packageSource: '' }],
};

export const cacheKey = 'fullUserProfileProviderQuery';
const query = graphql(/* GraphQL */ `
    query UserFullProfile {
        userFullProfile {
            activeProfile {
                id
                isDeleted
                locale
                isLocked
                isMasterProfile
                name
                profilePic
                orderId
                profilePicPath
                profilePicSmall
                profilePicMedium
                referenceProfileId
                profileType
                screenTimeLimitEnabled
                screenTimeLimitSeconds
                userId
            }
            entitlement {
                multiPackageTracking
            }
            email
            sha256EmailHash
            bundleStatusTracking
            packageStatusTracking
            cbsPackageInfo {
                packageSource
                packageStatus
                billingCadence
                planType
                productName
                packageCode
                vendorCode
            }
            accountProfiles {
                id
                isDeleted
                isLocked
                isMasterProfile
                locale
                name
                profilePic
                orderId
                profilePicPath
                profilePicSmall
                profilePicMedium
                profileType
                referenceProfileId
                screenTimeLimitEnabled
                screenTimeLimitSeconds
                userId
                personalizationId
            }
            userStatus {
                description
            }
            providerUserId
            paramountStreamingId
            parentalControl
            requirePinSwitchProfileEnabled
            kidsProfileButton
            userRegistrationCountry
            adsRegion
            isLoggedIn
            allRatings
            nflLTSOptInTime
            nflLTSOptIn
            optIn
        }
    }
`);

const useFullUser = () => {
    const { data, isLoading, isError, isFetched } = useRequest({
        cacheKey,
        query,
        appName: 'user-profiles',
    });
    const { region } = useRegion();

    const userData = useMemo(() => buildFullUserProfile(data, region), [data, region]);

    return {
        ...userData,
        isLoading,
        isError,
        isFetched,
    };
};

/**
 * Shapes the raw `UserFullProfile` GraphQL payload response into UI-ready data with derived flags and fallbacks.
 *
 * `anonymousRegion` (optional) sets `userRegistrationCountry` on the anonymous fallback only — the
 * caller-resolved region (client: `useRegion`; SSR: `getRegion(req)`). When omitted, the shared
 * `US`-default `ANON_PROFILE` is returned as-is (never mutated), preserving existing behaviour.
 */
const buildFullUserProfile = (data?: UserFullProfileQuery, anonymousRegion?: string) => {
    // isRegistered requires the login flag while isSubscriber/isExSubscriber are status-only —
    // historical semantics preserved on purpose, see user-predicates.ts.
    const isRegistered = isLoggedInWithStatus(data?.userFullProfile, [USER_STATUS.REGISTERED]);

    const isSubscriber = isSubscriberUser(data?.userFullProfile);
    const isExSubscriber = isExSubscriberUser(data?.userFullProfile);

    const isKidProfile =
        data?.userFullProfile?.activeProfile?.profileType === ProfileType.Kids ||
        data?.userFullProfile?.activeProfile?.profileType === ProfileType.YoungerKids;

    const anonymousProfile = anonymousRegion
        ? { ...ANON_PROFILE, userRegistrationCountry: anonymousRegion.toUpperCase() }
        : ANON_PROFILE;

    return {
        userFullProfile: (data?.userFullProfile as FullUser) || anonymousProfile,
        activeProfile: data?.userFullProfile?.activeProfile as UserProfile,
        profiles: data?.userFullProfile?.accountProfiles as UserProfile[],
        masterProfile: data?.userFullProfile?.accountProfiles?.find(
            (profile: UserProfile | null) => profile?.isMasterProfile,
        ) as UserProfile,
        requirePinSwitchProfileEnabled: data?.userFullProfile
            ?.requirePinSwitchProfileEnabled as boolean,
        isLoggedIn: data?.userFullProfile?.isLoggedIn as boolean,
        isKidProfile,
        isAnonymous: isAnonymousUser(data?.userFullProfile),
        isRegistered,
        isSubscriber,
        isExSubscriber,
        cbsPackageInfo: data?.userFullProfile?.cbsPackageInfo?.[0],
    };
};

// Used with getServerSideProps
const prefetchFullUser = (req: IncomingMessage) => {
    return prefetchRequest({
        cacheKey,
        query,
        appName: 'user-profiles',
        req,
    });
};

export { ANON_PROFILE, buildFullUserProfile, useFullUser as default, prefetchFullUser };
